import { firebaseShared } from "@shared/firebase.js";
import * as firebase from "@shared/firebase.js";
import * as environment from "@shared/environment.js";
import { dlog } from "@shared/utility.js";
import { priceLookup } from "@shared/priceLookup.js";
import { renderSpecWithID, fetchRenderSpecs } from "@shared/renderSpecFeed.js";

let allProductSKUs = [];
let disabledInventory = new Set();

function setDisabledInventory(skuIDs) {
    disabledInventory = new Set(skuIDs || []);

    dlog(`disabledInventory: ${[...disabledInventory].join(", ")}`);
}

function isDisabledInventory(skuID) {
    return disabledInventory.has(skuID);
}

function productSKUWithID(id) {
    return allProductSKUs.find(item => item.id === id);
}

function getAllProductSKUs() {
    return allProductSKUs ? [...allProductSKUs] : [];
}

function getLiveProductSKUs() {
    return allProductSKUs.filter(sku => sku.isLive === true);
}

function getProductSKUsByCategory(category) {
    return allProductSKUs.filter(sku => sku.productCategory === category);
}

function getProductSKUsByRenderSpec(renderSpecID) {
    return allProductSKUs.filter(sku => sku.renderSpec === renderSpecID);
}

function getFirstLiveSKUFromOrdering(ordering, ignoreInventory = false) {
    if (!ordering || ordering.length === 0) {
        return null;
    }
    for (const skuID of ordering) {
        const sku = productSKUWithID(skuID);
        if (sku && sku.isLive !== false && (ignoreInventory || !disabledInventory.has(sku.id))) {
            return sku;
        }
    }
    return null;
}

async function fetchProductSKUs(force = false) {
    if (!force && allProductSKUs.length > 0) {
        dlog("already fetched product SKUs. bailing.");
        return;
    }

    try {
        dlog(`fetchProductSKUs: start; collection: ${environment.currentProductSKUCollectionName()}`);

        const collectionRef = firebase.collection(firebaseShared.db, environment.currentProductSKUCollectionName());
        dlog(`fetchProductSKUs: got collectionRef`);

        const snapshot = await firebaseShared.withRetry(() => firebase.getDocs(collectionRef));
        dlog(`fetchProductSKUs: got snapshot`);

        const tempSKUs = [];

        snapshot.forEach(doc => {
            tempSKUs.push(doc.data());
        });

        allProductSKUs = tempSKUs;
        dlog("allProductSKUs count: " + allProductSKUs.length);
    } catch (error) {
        console.warn("fetchProductSKUs failed after retries:", error);
        return;
    }

    dlog(`fetchProductSKUs: ${allProductSKUs.length} items in collection`);

    // Ensure RenderSpecs are loaded, then merge their properties into ProductSKUs
    await fetchRenderSpecs();
    mergeRenderSpecProperties();

    await fetchPricesForProductSKUs();

    // DEBUG: simulate all grip-magsafe SKUs OOS
    // ["702312", "702313", "702314", "702316", "702317"].forEach(id => disabledInventory.add(id));
    
    // DEBUG: simulate all grip-adhesive SKUs OOS
    // ["701453", "701472", "701683", "701684", "701685", "701686", "701687", "701688"].forEach(id => disabledInventory.add(id));
    
    // DEBUG: simulate all grip-poptop SKUs OOS
    // ["701550", "701551"].forEach(id => disabledInventory.add(id));
    
    // DEBUG: simulate all wallets OOS
    // ["805776", "805777"].forEach(id => disabledInventory.add(id));
}

// Merge RenderSpec properties into ProductSKUs so rendering code has access to all needed fields
function mergeRenderSpecProperties() {
    for (const sku of allProductSKUs) {
        if (!sku.renderSpec) continue;

        const spec = renderSpecWithID(sku.renderSpec);
        if (!spec) {
            console.warn(`RenderSpec not found for ProductSKU ${sku.id}: ${sku.renderSpec}`);
            continue;
        }

        // Merge all RenderSpec properties into the ProductSKU (SKU properties take precedence)
        for (const key of Object.keys(spec)) {
            if (key === "id") continue; // Don't overwrite SKU's id
            if (!(key in sku)) {
                sku[key] = spec[key];
            }
        }
    }
    dlog("Merged RenderSpec properties into ProductSKUs");
}

async function fetchPricesForProductSKUs() {
    if (!priceLookup.priceEndpointCallback || allProductSKUs.length === 0) {
        console.warn("no price endpoint");
        return;
    }

    try {
        const disabledInventoryList = await priceLookup.fetchPrices(allProductSKUs);
        if (disabledInventoryList) {
            setDisabledInventory(disabledInventoryList);
        }
    } catch (error) {
        console.warn("fetchPricesForProductSKUs: Failed to fetch prices:", error);
    }
}

export { productSKUWithID, fetchProductSKUs, getAllProductSKUs, getLiveProductSKUs, getProductSKUsByCategory, getProductSKUsByRenderSpec, getFirstLiveSKUFromOrdering, setDisabledInventory, isDisabledInventory };
